import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { sanitizeSprInput, sprInputIssue } from '../../constants';

interface SprInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Digital SAT student-produced response. Up to 5 characters (6 with a minus
 * sign): digits, one decimal point or one fraction bar. The preview shows how
 * the answer will be read, like the real test's "Answer Preview".
 */
export function SprInput({ value, onChange, disabled }: SprInputProps) {
  const { t } = useTranslation();
  const id = useId();
  const issue = value ? sprInputIssue(value) : null;
  const preview = value && !issue ? value : null;

  return (
    <div className="max-w-xs space-y-3">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {t('exam.spr.label')}
      </label>
      <input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(sanitizeSprInput(e.target.value))}
        aria-invalid={Boolean(issue)}
        aria-describedby={`${id}-help`}
        className={cn(
          'h-14 w-44 rounded-md border-2 border-gray-900 bg-white px-3 font-mono text-2xl tracking-widest text-gray-900',
          issue && 'border-red-600',
        )}
      />
      <div
        className="flex h-10 items-center gap-2 rounded-md bg-gray-50 px-3 text-sm text-gray-700"
        aria-live="polite"
      >
        <span className="text-gray-500">{t('exam.spr.preview')}</span>
        <span className="font-mono text-base font-semibold text-gray-900">{preview ?? '—'}</span>
      </div>
      <p id={`${id}-help`} className={cn('text-xs', issue ? 'text-red-600' : 'text-gray-500')}>
        {issue === 'INVALID' || issue === 'ZERO_DENOMINATOR' ? t('exam.spr.invalid') : t('exam.spr.help')}
      </p>
    </div>
  );
}

export default SprInput;
